import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Award, Shield, Users, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { stats } from '../../data/testimonials';

const AboutSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const features = [
    {
      icon: Shield,
      title: "Premium Materials",
      description: "Only the finest hardwoods and UV-resistant epoxy resins"
    },
    {
      icon: Award,
      title: "Master Craftsmanship",
      description: "Handcrafted by skilled artisans with decades of experience"
    },
    {
      icon: Users,
      title: "Custom Solutions",
      description: "Tailored designs that perfectly match your vision"
    },
    {
      icon: Zap,
      title: "Lifetime Support",
      description: "Comprehensive care guide and maintenance support"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <section className="section-padding bg-dark-800 relative overflow-hidden">
      <div className="container-custom">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div variants={itemVariants}>
              <div className="text-accent-400 text-lg font-medium mb-4">
                About ResinCraft Studio
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold font-serif text-white mb-6 leading-tight">
                Crafting Excellence 
                <span className="text-gradient"> Since 2018</span>
              </h2>
              
              <p className="text-xl text-dark-200 mb-6 leading-relaxed">
                At ResinCraft Studio, we specialize in creating extraordinary furniture pieces that blend the natural beauty of wood with the endless possibilities of epoxy resin.
              </p>
              
              <p className="text-lg text-dark-300 mb-8 leading-relaxed">
                Each piece is a unique work of art, meticulously handcrafted by our master artisans. Our process combines traditional woodworking techniques with innovative resin artistry to create furniture that is not only functional but also a stunning focal point for any space.
              </p>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-6 mb-8">
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    variants={itemVariants}
                    className="text-center lg:text-left"
                  >
                    <div className="text-3xl font-bold text-accent-400 mb-2">
                      {stat.number}
                    </div>
                    <div className="text-dark-300 font-medium mb-1">
                      {stat.label}
                    </div>
                    <div className="text-dark-400 text-sm">
                      {stat.description}
                    </div>
                  </motion.div>
                ))}
              </div>
              
              <Link
                to="/about"
                className="inline-flex items-center space-x-2 btn-primary"
              >
                <span>Learn More About Our Process</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </motion.div>

            {/* Right Content - Image Grid */}
            <motion.div variants={itemVariants} className="relative">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-6">
                  <motion.div
                    variants={itemVariants}
                    className="aspect-[4/5] rounded-2xl overflow-hidden group"
                  >
                    <img
                      src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=500&fit=crop"
                      alt="Crafting process"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </motion.div>
                  <motion.div
                    variants={itemVariants}
                    className="aspect-[4/3] rounded-2xl overflow-hidden group"
                  >
                    <img
                      src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=400&h=300&fit=crop"
                      alt="Finished product"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </motion.div>
                </div>
                <div className="space-y-6 mt-12">
                  <motion.div
                    variants={itemVariants}
                    className="aspect-[4/3] rounded-2xl overflow-hidden group"
                  >
                    <img
                      src="https://images.unsplash.com/photo-1506439773649-6e0eb8cfb237?w=400&h=300&fit=crop"
                      alt="Wood selection"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </motion.div>
                  <motion.div
                    variants={itemVariants}
                    className="aspect-[4/5] rounded-2xl overflow-hidden group"
                  >
                    <img
                      src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=500&fit=crop"
                      alt="Epoxy pouring"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                  </motion.div>
                </div>
              </div>

              {/* Floating Card */}
              <motion.div
                variants={itemVariants}
                className="absolute -bottom-6 -left-6 bg-dark-900/90 backdrop-blur-md rounded-2xl p-6 border border-accent-500/30"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-accent rounded-full flex items-center justify-center">
                    <Award className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="text-white font-semibold">Award Winning</div>
                    <div className="text-dark-300 text-sm">Best Furniture Design 2023</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>

          {/* Features Section */}
          <motion.div variants={containerVariants} className="mt-24">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="text-center group"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-accent rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300">
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-dark-300 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Background Elements */}
      <div className="absolute top-1/4 right-0 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent-600/5 rounded-full blur-3xl"></div>
    </section>
  );
};

export default AboutSection;